import type { BigIntish, Fetchable } from '@morpho-org/blue-sdk'

import { type InterfaceToken, toBigInt } from '@morpho-org/dapp-core'
import { formatLongString, isDefined } from '@morpho-org/morpho-ts'
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils'
import { Badge } from '@repo/ui'
import { forwardRef } from 'react'

export interface CellTokenPriceProps {
  /** Token object with decimals, price, symbol info */
  token?: InterfaceToken | null
  /** Token amount */
  amount: Fetchable<BigIntish>
  /** USD value (18 decimals) */
  value?: Fetchable<bigint>
}

/**
 * CellTokenPrice — displays a token amount with its USD equivalent.
 *
 * @example
 * ```tsx
 * <CellTokenPrice
 *   token={token}
 *   amount={1000000000000000000n}
 *   value={1500000000000000000000n}
 * />
 * ```
 */
const CellTokenPrice = forwardRef<HTMLDivElement, CellTokenPriceProps>(
  ({ token, amount, value, ...props }, ref) => {
    const formattedSymbol = formatLongString(token?.symbol ?? '', 16)

    const formattedAmount = (() => {
      if (!isDefined(amount) || !isDefined(token) || !isDefined(token.decimals)) {
        return '-'
      }
      const amountBigInt = toBigInt(amount)

      return formatters.short
        .digits(getDigitsFromPriceAndAmount(token.price, amountBigInt, token.decimals))
        .default('-')
        .unit(formattedSymbol)
        .of(amountBigInt, token.decimals)
    })()

    const formattedValue = formatters.short.unit('$').digits(2).of(value, 18)

    return (
      <div ref={ref} className="gap-2xs flex flex-col items-start" {...props}>
        <span className="text-foreground text-xs">{formattedAmount}</span>
        {formattedAmount !== '-' && formattedValue && (
          <Badge variant="tertiary" size="sm">
            {formattedValue}
          </Badge>
        )}
      </div>
    )
  }
)
CellTokenPrice.displayName = 'CellTokenPrice'

export default CellTokenPrice
